import type { BaseQueryApi, BaseQueryExtraOptions } from "@reduxjs/toolkit/query";
import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";

import { hideNotification, showErrorNotification } from "../../../components/NotificationAlerts";
import { baseApi } from "../../service/baseApi";
import { routes } from "../config/routes";
import { authActions } from "./actions";
import { getAccessToken, getUserNameFromToken, setAccessToken, setRefreshToken } from "./index";
import { permissionsReset } from "./permissions";
import { deleteAssignedDataGroup, deleteUserRights, getAssignedDataGroup, setIdToken, setUserRights } from "./SecurityManager";
import type { AuthenticationRequest, LoginResponse } from "./types";

export const loginApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        login: builder.mutation<null, AuthenticationRequest>({
            queryFn: async (
                arg: AuthenticationRequest,
                api: BaseQueryApi,
                extraOptions: BaseQueryExtraOptions<BaseQueryFn>,
                baseQuery: BaseQueryFn,
            ) => {
                try {
                    const resp = await baseQuery(
                        {
                            url: "/login",
                            method: "POST",
                            body: arg,
                        },
                        api,
                        extraOptions,
                    );

                    const loginResponse = resp.data as LoginResponse;

                    if (loginResponse && loginResponse.jwt && loginResponse.refreshToken) {
                        setAccessToken(loginResponse.jwt);
                        setRefreshToken(loginResponse.refreshToken);
                        setUserRights(loginResponse.userRights);
                        const user = getUserNameFromToken();
                        if (user) {
                            api.dispatch(authActions.setUserName(user));
                        }
                        api.dispatch(authActions.redirect(routes.landingPage.path));
                    } else {
                        api.dispatch(showErrorNotification({ text: "common.unauthorisedBadCredentials" }));
                    }
                } catch (err) {
                    // console.log(err);
                    api.dispatch(showErrorNotification({ text: "common.unknown" }));
                }
                return { data: null };
            },
        }),
        logout: builder.mutation<null, unknown>({
            queryFn: async (
                _arg: AuthenticationRequest,
                api: BaseQueryApi,
                extraOptions: BaseQueryExtraOptions<BaseQueryFn>,
                baseQuery: BaseQueryFn,
            ) => {
                const token = getAccessToken();
                if (token) {
                    await baseQuery(
                        {
                            url: "/logout",
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                            body: {
                                dataGroup: getAssignedDataGroup(),
                            },
                        },
                        api,
                        extraOptions,
                    );
                }

                setAccessToken(null);
                setRefreshToken(null);
                setIdToken(null);
                deleteUserRights();
                deleteAssignedDataGroup();
                permissionsReset();
                localStorage.clear();

                api.dispatch(hideNotification());
                api.dispatch(authActions.redirect(routes.login.path));

                return { data: null };
            },
        }),
    }),
});

export const { useLoginMutation, useLogoutMutation } = loginApi;
